import { qsa, el } from '../utils/dom.js';
import { PSDImportDialog } from './PSDImportDialog.js';
import { PackTilesetDialog } from './PackTilesetDialog.js';

/**
 * Menu bar — File, Edit, View, Generate, Help dropdowns.
 */
export class MenuBar {
  constructor(state, fileManager, commandHistory, tilesetLoader, compositor, { newMapDialog, generatorPanel, exportDialog, resizeDialog, pluginAPI, pixelLabPanel, engineExport, githubDialog, helpSystem, tutorial, tilesetLibrary, tileTaggerPanel, undoHistoryPanel } = {}) {
    this.state = state;
    this.fileManager = fileManager;
    this.commandHistory = commandHistory;
    this.tilesetLoader = tilesetLoader;
    this._tilesetLibrary = tilesetLibrary || null;
    this._tileTaggerPanel = tileTaggerPanel || null;
    this.compositor = compositor;
    this._pluginAPI = pluginAPI || null;
    this._activeMenu = null;

    const newMapAction = newMapDialog
      ? () => newMapDialog.show()
      : () => this._newMap();

    const generateItems = generatorPanel
      ? [
          { label: 'Generate Map...', action: () => generatorPanel.show() },
          { separator: true },
          { label: 'Generate Tileset (PixelLab)...', action: () => pixelLabPanel?.show() },
        ]
      : [{ label: 'Generators coming in v0.5', disabled: true }];

    this._menus = {
      file: [
        { label: 'New Map...', action: newMapAction, shortcut: 'Ctrl+N' },
        { label: 'Open...', action: () => fileManager.openFile(), shortcut: 'Ctrl+O' },
        { separator: true },
        { label: 'Save', action: () => fileManager.saveFile(), shortcut: 'Ctrl+S' },
        { label: 'Save As...', action: () => fileManager.saveFileAs(), shortcut: 'Ctrl+Shift+S' },
        { separator: true },
        { label: 'Add Tileset...', action: () => this._addTileset() },
        { label: 'Pack Tileset from Images...', action: () => this._packTilesetFromImages() },
        { label: 'Tag Tileset (TileMeta)...', action: () => this._openTagger() },
        { separator: true },
        { label: 'Export as PNG...', action: () => exportDialog?.export() },
        { label: 'Export as TMX...', action: () => fileManager.exportAsTMX() },
        { label: 'Export for Engine...', action: () => engineExport?.show() },
        { label: 'Resize Map...', action: () => resizeDialog?.show() },
        { separator: true },
        { sectionHeader: 'GitHub' },
        { label: 'Save to GitHub...', action: () => githubDialog?.showSave() },
        { label: 'Load from GitHub...', action: () => githubDialog?.showLoad() },
      ],
      edit: [
        { label: 'Undo', action: () => { commandHistory.undo(); compositor.requestRender(); }, shortcut: 'Ctrl+Z' },
        { label: 'Redo', action: () => { commandHistory.redo(); compositor.requestRender(); }, shortcut: 'Ctrl+Y' },
        { separator: true },
        { label: 'History Panel', action: () => undoHistoryPanel?.toggle(), shortcut: 'Ctrl+H' },
      ],
      view: [
        { label: 'Toggle Grid', action: () => { state.set('showGrid', !state.get('showGrid')); compositor.requestRender(); }, shortcut: '#' },
        { label: 'Toggle Properties', action: () => { state.set('showProperties', !state.get('showProperties')); compositor.requestRender(); } },
        { separator: true },
        { label: 'Zoom In', action: () => { state.set('zoom', Math.min(4, state.get('zoom') + 0.25)); compositor.requestRender(); }, shortcut: '+' },
        { label: 'Zoom Out', action: () => { state.set('zoom', Math.max(0.25, state.get('zoom') - 0.25)); compositor.requestRender(); }, shortcut: '-' },
        { label: 'Reset View', action: () => { state.batch({ zoom: 1, scrollX: 0, scrollY: 0 }); compositor.requestRender(); }, shortcut: 'Ctrl+0' },
      ],
      generate: generateItems,
      help: [
        { label: 'User Guide', action: () => window.open('help/index.html', '_blank') },
        { label: 'Getting Started', action: () => window.open('help/getting-started.html', '_blank') },
        { label: 'Keyboard Shortcuts', action: () => window.open('help/keyboard-shortcuts.html', '_blank'), shortcut: 'F1' },
        { separator: true },
        { label: 'Tutorial: Getting Started', action: () => tutorial?.start('getting-started') },
        { label: 'Tutorial: Importing Tilesets', action: () => tutorial?.start('import-tileset') },
        { separator: true },
        { label: 'About TileForge', action: () => this._showAboutDialog() },
      ],
    };

    this._bindMenuItems();
    document.addEventListener('click', () => this._closeMenus());
  }

  _bindMenuItems() {
    const items = qsa('#menu-bar .menu-item');
    for (const item of items) {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const menuName = item.dataset.menu;
        if (this._activeMenu === menuName) {
          this._closeMenus();
          return;
        }
        this._showMenu(item, menuName);
      });
      item.addEventListener('mouseenter', () => {
        if (this._activeMenu) {
          const menuName = item.dataset.menu;
          this._showMenu(item, menuName);
        }
      });
    }
  }

  _showMenu(anchorEl, menuName) {
    this._closeMenus();
    let items = this._menus[menuName];
    if (!items) return;

    // Inject recent files into File menu
    if (menuName === 'file') {
      const recent = this.fileManager.recentFiles.list();
      if (recent.length > 0) {
        items = [...items,
          { separator: true },
          { sectionHeader: 'Recent Maps' },
          ...recent.map(entry => ({
            label: entry.fileName.replace(/\.[^.]+$/, ''),
            action: () => this._openRecentFile(entry),
          })),
        ];
      }
    }

    // Append plugin-registered menu items
    if (this._pluginAPI) {
      const pluginItems = this._pluginAPI.getMenuItems(menuName);
      if (pluginItems.length > 0) {
        items = [...items, { separator: true }, ...pluginItems];
      }
    }

    this._activeMenu = menuName;
    anchorEl.classList.add('open');

    const dropdown = el('div', { className: 'menu-dropdown' });
    for (const item of items) {
      if (item.separator) {
        dropdown.appendChild(el('div', { className: 'menu-separator' }));
        continue;
      }
      if (item.sectionHeader) {
        dropdown.appendChild(el('div', { className: 'menu-section-header' }, item.sectionHeader));
        continue;
      }
      const menuItem = el('div', {
        className: `menu-dropdown-item${item.disabled ? ' disabled' : ''}`,
      });
      menuItem.appendChild(el('span', {}, item.label));
      if (item.shortcut) {
        menuItem.appendChild(el('span', { className: 'menu-shortcut' }, item.shortcut));
      }
      if (!item.disabled) {
        menuItem.addEventListener('click', (e) => {
          e.stopPropagation();
          this._closeMenus();
          item.action();
        });
      }
      dropdown.appendChild(menuItem);
    }

    const rect = anchorEl.getBoundingClientRect();
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.top = `${rect.bottom}px`;
    document.body.appendChild(dropdown);
  }

  _closeMenus() {
    this._activeMenu = null;
    qsa('#menu-bar .menu-item').forEach(i => i.classList.remove('open'));
    qsa('.menu-dropdown').forEach(d => d.remove());
  }

  _openTagger() {
    if (!this._tileTaggerPanel) return;
    const tilesets = this.state.get('tilesets');
    if (tilesets.length === 0) {
      alert('Load a tileset first (File > Add Tileset).');
      return;
    }
    // Open tagger in a dialog overlay
    const overlay = document.createElement('div');
    overlay.className = 'dialog-overlay';
    const dialogEl = document.createElement('div');
    dialogEl.className = 'dialog';
    dialogEl.style.cssText = 'min-width:900px;max-width:95vw;height:85vh;overflow:hidden;padding:0;';
    overlay.appendChild(dialogEl);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);

    this._tileTaggerPanel.container = dialogEl;
    this._tileTaggerPanel.setOnClose(() => overlay.remove());
    // Load existing meta if saved on tileset
    const ts = tilesets[0];
    const existingMeta = ts.tileMeta || null;
    this._tileTaggerPanel.open(0, existingMeta);
  }

  _addTileset() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.webp,.psd';
    input.addEventListener('change', async () => {
      const file = input.files[0];
      if (!file) return;

      // PSD files get the educational import dialog
      if (file.name.toLowerCase().endsWith('.psd')) {
        const psdDialog = new PSDImportDialog(this.state, this.tilesetLoader, this.compositor);
        psdDialog.show(file, (name) => {
          this._flashStatus(`Tileset "${name}" imported from PSD`);
        });
        return;
      }

      // Normal image tileset
      if (this._tilesetLibrary) {
        const meta = await this._tilesetLibrary.addFromFile(file);
        if (meta) {
          await this._tilesetLibrary.loadIntoEditor(meta.id);
          this.compositor.requestRender();
          this._flashStatus(`Tileset "${meta.name || file.name}" loaded`);
        }
      } else {
        this.tilesetLoader.addFromFile();
      }
    });
    input.click();
  }

  _packTilesetFromImages() {
    const packDialog = new PackTilesetDialog(this.state, this.compositor);
    packDialog.pickAndShow((name) => {
      this._flashStatus(`Tileset "${name}" packed from images`);
    });
  }

  async _openRecentFile(entry) {
    // If we have a snapshot, load directly
    if (entry.snapshot) {
      try {
        const tmj = JSON.parse(entry.snapshot);
        await this.fileManager._loadTMJ(tmj, entry.fileName);
        this.compositor.requestRender();
        this._flashStatus(`Opened "${entry.fileName.replace(/\.[^.]+$/, '')}"`);
        return;
      } catch (err) {
        console.warn('Failed to load from snapshot, prompting for file:', err);
      }
    }

    // No snapshot — try File System Access API to reopen
    if ('showOpenFilePicker' in window) {
      try {
        const [handle] = await window.showOpenFilePicker({
          types: [{ description: 'Tiled Map JSON', accept: { 'application/json': ['.tmj', '.json'] } }],
        });
        const file = await handle.getFile();
        const text = await file.text();
        const tmj = JSON.parse(text);
        this.fileManager._fileHandle = handle;
        await this.fileManager._loadTMJ(tmj, file.name);
        this.compositor.requestRender();
        this._flashStatus(`Opened "${file.name.replace(/\.[^.]+$/, '')}"`);
      } catch (err) {
        if (err.name !== 'AbortError') {
          this._flashStatus('Could not open file');
        }
      }
    } else {
      // Fallback — prompt user to find the file
      this.fileManager.openFile();
    }
  }

  _newMap() {
    const width = prompt('Map width (tiles):', '40');
    const height = prompt('Map height (tiles):', '30');
    if (!width || !height) return;
    this.state.createBlankMap(parseInt(width, 10), parseInt(height, 10));
    this.compositor.requestRender();
  }

  _flashStatus(message, duration = 3000) {
    // Status bar update
    const statusTool = document.getElementById('status-tool');
    if (statusTool) {
      const prev = statusTool.textContent;
      statusTool.textContent = message;
      statusTool.style.color = '#2ecc71';
      setTimeout(() => {
        statusTool.textContent = prev;
        statusTool.style.color = '';
      }, duration);
    }

    // Toast notification — more visible than status bar alone
    const toast = document.createElement('div');
    toast.style.cssText = 'position:fixed;bottom:48px;left:50%;transform:translateX(-50%);z-index:9999;padding:10px 20px;background:#2ecc71;color:#fff;font-size:13px;font-weight:500;border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,0.3);opacity:0;transition:opacity 0.3s;';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => { toast.style.opacity = '1'; });
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  _showAboutDialog() {
    const overlay = el('div', { className: 'about-overlay' });
    const dialog = el('div', { className: 'about-dialog' });
    dialog.innerHTML = `
      <div class="about-header">
        <img src="/echoforge-logo.png" alt="EchoForge" class="about-logo" />
        <div>
          <div class="about-title">TileForge Studio</div>
          <div class="about-version">v1.2</div>
        </div>
      </div>
      <div class="about-body">
        <p>Browser-based 2D tile map editor with procedural generation, Tiled-compatible file formats, and game engine export.</p>
        <p class="about-credit">Built by <strong>EchoForge Studios</strong></p>
        <div class="about-links">
          <a href="help/index.html" target="_blank">User Guide</a>
          <a href="https://echoforge.games" target="_blank">EchoForge Studios</a>
        </div>
      </div>
      <button class="about-close">Close</button>
    `;
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    const close = () => overlay.remove();
    dialog.querySelector('.about-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  }
}
